import type { BillingModule, BillingPlan } from "./types";
import type {
  BillingCancelCoreModuleDialogViewModel,
  BillingCancelDialogViewModel,
  BillingCancelTrialDialogViewModel,
  BillingEntityActionDialogViewModel,
  BillingPauseDialogViewModel,
  BillingRemoveModuleDialogViewModel,
} from "../types";
import { cancellationBoundaryFor, pauseBoundaryFor } from "./boundary";
import { entityFor, subscriptionPeriodEndFor } from "./catalog";
import { PAUSE_PLAN_MONTHLY_PRICE_USD, UNLIMITED_ID } from "./constants";
import { formatModuleList } from "./formatting";
import type { BillingCatalog, BillingSnapshot } from "./types";

type BillingEntity = BillingPlan | BillingModule;

type CancelDialogInput = Readonly<{
  catalog: BillingCatalog;
  commitmentStartAt?: number;
  entity?: BillingEntity | null;
  kind: "cancel";
  nowSec: number;
  snapshot: BillingSnapshot;
  timing: "immediate" | "scheduled";
}>;

type PauseDialogInput = Readonly<{
  catalog: BillingCatalog;
  entityId: string;
  kind: "pause";
  nowSec: number;
  snapshot: BillingSnapshot;
}>;

type RemoveModuleDialogInput = Readonly<{
  catalog: BillingCatalog;
  kind: "remove-module";
  moduleId: string;
  nowSec: number;
  snapshot: BillingSnapshot;
}>;

type CancelCoreModuleDialogInput = Readonly<{
  catalog: BillingCatalog;
  kind: "cancel-core-module";
  moduleId: string;
  nowSec: number;
  snapshot: BillingSnapshot;
  step: "warning" | "confirm";
}>;

type CancelTrialDialogInput = Readonly<{
  catalog: BillingCatalog;
  kind: "cancel-trial";
  moduleId: string;
}>;

export type EntityActionDialogInput =
  | CancelCoreModuleDialogInput
  | CancelDialogInput
  | CancelTrialDialogInput
  | PauseDialogInput
  | RemoveModuleDialogInput;

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const projectCancelDialog = ({
  catalog,
  commitmentStartAt,
  entity,
  nowSec,
  snapshot,
  timing,
}: CancelDialogInput): BillingCancelDialogViewModel | null => {
  if (!entity) {
    return null;
  }
  if (timing === "immediate") {
    return {
      body: [
        "Are you sure you want to cancel your plan? This will take effect at the end of your current billing period.",
      ],
      cancelLabel: "Keep my plan",
      commitmentDate: null,
      confirmLabel: "Yes, cancel",
      effectiveAt: null,
      effectiveDate: null,
      entityId: entity.id,
      entityName: entity.name,
      kind: "cancel",
      title: "Cancel plan",
      timing,
    };
  }

  const cancellation = cancellationBoundaryFor({
    catalog,
    commitmentStartAt,
    entity,
    nowSec,
    snapshot,
  });
  if (!cancellation) {
    return {
      body: [
        `Your current plan has a ${entity.commitment}-month minimum commitment.`,
        "We'll schedule your cancellation for the earliest possible date. You'll keep full access until then and can undo anytime.",
      ],
      cancelLabel: "Keep my plan",
      commitmentDate: null,
      confirmLabel: "Schedule cancellation",
      disabledReason: "Cancellation date unavailable. Please contact support.",
      effectiveAt: null,
      effectiveDate: null,
      entityId: entity.id,
      entityName: entity.name,
      kind: "cancel",
      title: "Are you sure you want to schedule cancellation?",
      timing,
    };
  }

  const commitmentDate = formatDate(
    new Date(cancellation.commitmentEnd * 1_000)
  );
  const effectiveDate = formatDate(new Date(cancellation.boundary * 1_000));
  const lands =
    cancellation.commitmentEnd > nowSec
      ? "the end of the first billing period after your minimum commitment"
      : "the end of your current billing period";
  return {
    body: [
      `Your current plan has a ${entity.commitment}-month minimum commitment ending ${commitmentDate}.`,
      `We'll schedule your cancellation for ${lands}: ${effectiveDate}. You'll keep full access until then and can undo anytime.`,
    ],
    cancelLabel: "Keep my plan",
    commitmentDate,
    confirmLabel: `Schedule cancellation for ${effectiveDate}`,
    effectiveAt: cancellation.boundary,
    effectiveDate,
    entityId: entity.id,
    entityName: entity.name,
    kind: "cancel",
    title: "Are you sure you want to schedule cancellation?",
    timing,
  };
};

const pauseScopeSentence = (
  entity: BillingEntity,
  catalog: BillingCatalog
): string => {
  const scope =
    entity.type === "module"
      ? `Pausing puts your entire plan on hold, not just ${entity.name}.`
      : `Pausing puts your entire ${entity.name} plan on hold.`;
  const pausedAlongside = catalog.activeModules
    .filter(({ id }) => entity.type === "plan" || id !== entity.id)
    .map(({ name }) => name);
  if (pausedAlongside.length === 0) {
    return scope;
  }

  return `${scope} ${formatModuleList(pausedAlongside)} will pause with it.`;
};

const PAUSE_PRICE_SENTENCE = `While paused, your plan is $${PAUSE_PLAN_MONTHLY_PRICE_USD}/month.`;

// The backend parks a self-serve pause at a period end of the target
// subscription — the first one after any commitment still running on it — and
// refuses one whose current period end has already passed, so that boundary is
// the only date the dialog can promise.
const projectPauseDialog = ({
  catalog,
  entityId,
  nowSec,
  snapshot,
}: PauseDialogInput): BillingPauseDialogViewModel | null => {
  const entity = entityFor(catalog, entityId);
  if (!entity) {
    return null;
  }

  const shared = {
    cancelLabel: "Keep my plan",
    entityId: entity.id,
    entityName: entity.name,
    kind: "pause",
    title: "Are you sure you want to pause your plan?",
  } as const;
  const scopeSentence = pauseScopeSentence(entity, catalog);

  const pause = pauseBoundaryFor({ catalog, entityId, nowSec, snapshot });
  if (!pause || pause.subscription.periodEnd <= nowSec) {
    return {
      ...shared,
      body: [
        scopeSentence,
        "We'll schedule your pause for the end of your current billing period. You'll keep full access until then and can undo anytime.",
        PAUSE_PRICE_SENTENCE,
      ],
      confirmLabel: "Schedule plan pause",
      disabledReason: "Pause date unavailable. Please contact support.",
      effectiveAt: null,
      effectiveDate: null,
    };
  }

  const effectiveDate = formatDate(new Date(pause.boundary * 1_000));
  const lands =
    pause.requestedAt === null
      ? "the end of your current billing period"
      : "the end of the first billing period after your minimum commitment";
  return {
    ...shared,
    body: [
      scopeSentence,
      `We'll schedule your pause for ${lands}: ${effectiveDate}. You'll keep full access until then and can undo anytime.`,
      PAUSE_PRICE_SENTENCE,
    ],
    confirmLabel: `Schedule plan pause for ${effectiveDate}`,
    effectiveAt: pause.boundary,
    effectiveDate,
  };
};

const projectRemoveModuleDialog = ({
  catalog,
  moduleId,
  nowSec,
  snapshot,
}: RemoveModuleDialogInput): BillingRemoveModuleDialogViewModel | null => {
  const module = catalog.activeModules.find(({ id }) => id === moduleId);
  if (!module) {
    return null;
  }

  const remainingModuleIds = catalog.activeModules
    .filter(({ id }) => id !== moduleId)
    .map(({ id }) => id);

  // A removal always executes at the boundary the merchant has already been
  // granted — the end of the cycle they paid for, or the end of a trial they
  // were never billed for. No proration or credit is ever promised.
  const boundary =
    module.trialUnconfirmable && module.trialEndAt !== undefined
      ? module.trialEndAt
      : subscriptionPeriodEndFor(snapshot, catalog, moduleId, nowSec);

  return {
    body: [
      module.trialUnconfirmable
        ? `You'll keep free access until your trial ends on ${formatDate(new Date(boundary * 1_000))}, and you won't be charged for it.`
        : `You'll keep full access through the end of your current billing cycle on ${formatDate(new Date(boundary * 1_000))}.`,
      `After that, ${module.name} will be removed from your plan. You can undo anytime before then.`,
    ],
    cancelLabel: "Keep my plan",
    confirmLabel: `Cancel ${module.name}`,
    currentPlanId:
      catalog.currentPlan?.id === UNLIMITED_ID ? UNLIMITED_ID : null,
    entityId: module.id,
    entityName: module.name,
    kind: "remove-module",
    remainingModuleIds,
    title: `Are you sure you want to cancel ${module.name}?`,
  };
};

const projectCancelTrialDialog = ({
  catalog,
  moduleId,
}: CancelTrialDialogInput): BillingCancelTrialDialogViewModel | null => {
  const module = catalog.activeModules.find(({ id }) => id === moduleId);
  if (!module) {
    return null;
  }

  return {
    // The default outcome leads, so ending the trial now is weighed against
    // what happens if the merchant does nothing.
    body: [
      ...(module.trialEndAt === undefined
        ? []
        : [
            module.trialConverts
              ? `This trial converts to paid on ${formatDate(new Date(module.trialEndAt * 1_000))}, and billing for ${module.name} starts then.`
              : `This trial ends on its own on ${formatDate(new Date(module.trialEndAt * 1_000))} at no cost.`,
          ]),
      `If you end it now, you'll lose access to ${module.name} today. This won't affect the rest of your plan.`,
    ],
    cancelLabel: "Keep trial",
    confirmLabel: "End trial now",
    entityId: module.id,
    entityName: module.name,
    kind: "cancel-trial",
    title: `End your ${module.name} trial now?`,
  };
};

const projectCancelCoreModuleDialog = ({
  catalog,
  moduleId,
  nowSec,
  snapshot,
  step,
}: CancelCoreModuleDialogInput): BillingCancelCoreModuleDialogViewModel | null => {
  const module = catalog.activeModules.find(({ id }) => id === moduleId);
  if (!module) {
    return null;
  }

  const moduleNames = catalog.activeModules.map(({ name }) => name);
  // The whole subscription is cancelled at the end of the cycle already paid
  // for.
  const effectiveDate = formatDate(
    new Date(
      subscriptionPeriodEndFor(snapshot, catalog, moduleId, nowSec) * 1_000
    )
  );

  const shared = {
    cancelLabel: "Keep my plan",
    effectiveDate,
    entityId: module.id,
    entityName: module.name,
    kind: "cancel-core-module",
    moduleNames,
  } as const;

  if (step === "warning") {
    return {
      ...shared,
      body: [
        `${module.name} includes core capabilities other modules depend on. You can cancel other active modules independently. Otherwise, continuing here will schedule your entire plan for cancellation.`,
      ],
      confirmLabel: "Cancel entire plan",
      step,
      title: `Canceling ${module.name} cancels your whole plan`,
    };
  }

  return {
    ...shared,
    body: [
      `You'll keep full access through the end of your current billing cycle on ${effectiveDate}.`,
      `After that, ${formatModuleList(moduleNames)} will be removed from your account. You can undo anytime before then.`,
    ],
    confirmLabel: "Confirm cancel entire plan",
    step,
    title: "Are you sure you want to cancel your entire plan?",
  };
};

export const projectEntityActionDialog = (
  input: EntityActionDialogInput
): BillingEntityActionDialogViewModel | null => {
  switch (input.kind) {
    case "cancel":
      return projectCancelDialog(input);
    case "cancel-core-module":
      return projectCancelCoreModuleDialog(input);
    case "pause":
      return projectPauseDialog(input);
    case "remove-module":
      return projectRemoveModuleDialog(input);
    case "cancel-trial":
      return projectCancelTrialDialog(input);
  }
};
